import {
  ChargeCardPayment,
  ChargeCardPaymentEdge,
  ChargeCardPaymentStatus,
  Maybe,
  Query,
} from '@varo/graphql-schema-types';
import { useEffect, useRef, useState } from 'react';
import GetChargeCardPaymentHistory from 'services/graphql/queries/Believe/GetChargeCardPaymentHistory';
import { formatLocalDate } from 'shared/utils/string/date';
import { CombinedError, useQuery } from 'urql';

interface TransferHistoryData {
  loading: boolean;
  error?: CombinedError;
  loadMore: () => void;
  paymentList?: ChargeCardPayment[];
  scheduledTransactions?: Record<string, ChargeCardPayment[]>;
  processedTransactions?: Record<string, ChargeCardPayment[]>;
  refetch: () => void;
  scheduledTransactionsLength?: number;
  processedTransactionsLength?: number;
  transactionLength?: number;
}

const usePaymentHistory = (): TransferHistoryData => {
  const [after, setAfter] = useState<string | null>();
  const [transfers, setTransfers] = useState<ChargeCardPayment[]>([]);
  const fetchingMoreRef = useRef(false);

  const [{ data, fetching: loading, error }, refetch] = useQuery<Query>({
    query: GetChargeCardPaymentHistory,
    requestPolicy: 'network-only',
    variables: {
      after: after,
      first: 20,
    },
  });
  const chargeCard = data?.myAccounts?.chargeCard;

  const isPaymentPending = (transaction: ChargeCardPayment) =>
    [
      ChargeCardPaymentStatus.Active,
      ChargeCardPaymentStatus.Pending,
      ChargeCardPaymentStatus.InsufficientFunds,
    ].includes(transaction?.status);

  const edges = chargeCard?.paymentHistory?.edges;

  const transactions = edges?.map(
    (edge: Maybe<ChargeCardPaymentEdge>): ChargeCardPayment | undefined =>
      edge?.node,
  );

  const newScheduledTransactions = (transactions || []).filter(
    (transaction: ChargeCardPayment): boolean => isPaymentPending(transaction),
  ) as ChargeCardPayment[];

  const newProcessedTransactions = (transactions || []).filter(
    (transaction: ChargeCardPayment): boolean => !isPaymentPending(transaction),
  ) as ChargeCardPayment[];

  const processedTransactionsLength = newProcessedTransactions?.length;
  const scheduledTransactionsLength = newScheduledTransactions?.length;

  // Group by (MM-YYYY)
  const groupByMonth = (transactionsList: ChargeCardPayment[]) => {
    return transactionsList.reduce(
      (acc, transaction) => {
        const monthYear = formatLocalDate(transaction?.date, 'MMM yyyy');
        if (!acc[monthYear]) {
          acc[monthYear] = [];
        }
        acc[monthYear].push(transaction);
        return acc;
      },
      {} as Record<string, ChargeCardPayment[]>,
    );
  };

  const groupedScheduledTransactions = groupByMonth(newScheduledTransactions);
  const groupedProcessedTransactions = groupByMonth(newProcessedTransactions);

  const loadMore = () => {
    if (
      fetchingMoreRef.current ||
      !chargeCard?.paymentHistory?.pageInfo?.hasNextPage
    ) {
      return;
    }
    fetchingMoreRef.current = true;
    setAfter(chargeCard?.paymentHistory?.pageInfo?.endCursor ?? null);
  };

  useEffect(() => {
    if (data) {
      const edges = chargeCard?.paymentHistory?.edges ?? [];
      setTransfers((prevItems) => [
        ...prevItems,
        ...edges.map((edge) => edge.node),
      ]);
      fetchingMoreRef.current = false;
    }
  }, [chargeCard?.paymentHistory?.edges, data]);

  return {
    loading,
    error,
    paymentList: transfers,
    scheduledTransactions: groupedScheduledTransactions,
    processedTransactions: groupedProcessedTransactions,
    loadMore,
    refetch,
    scheduledTransactionsLength,
    processedTransactionsLength,
    transactionLength: edges?.length,
  };
};

export default usePaymentHistory;
